import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, Hash, CheckCircle, AlertCircle, Loader2, Trash2, ScanLine } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface SerialCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (serialNumbers: string[]) => void;
  /** 'remove' = velg eksisterende serienumre (TransType=1), 'add' = registrer nye (TransType=2) */
  mode: 'remove' | 'add';
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
}

interface SerialItem {
  serialNo: string;
  binCode: string;
  warehouseId: string;
}

export const SerialCorrectionModal: React.FC<SerialCorrectionModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  mode,
  itemNo,
  itemDescription,
  binCode,
  warehouseId
}) => {
  const { t } = useTranslation();
  // Felles state
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);

  // Remove-modus state
  const [availableSerials, setAvailableSerials] = useState<SerialItem[]>([]);
  const [filteredSerials, setFilteredSerials] = useState<SerialItem[]>([]);
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Add-modus state
  const [enteredSerials, setEnteredSerials] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [checking, setChecking] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setError(null);
      setConfirming(false);
      setSearchTerm('');
      setCurrentInput('');
      setSelectedSerials(new Set());
      setEnteredSerials([]);
      setAvailableSerials([]);
      setFilteredSerials([]);
      setChecking(false);

      if (mode === 'remove') {
        loadAvailableSerials();
      }

      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter serials for remove-modus
  useEffect(() => {
    if (mode !== 'remove') return;
    if (!searchTerm.trim()) {
      setFilteredSerials(availableSerials);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredSerials(
        availableSerials.filter(s => s.serialNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableSerials, mode]);

  const loadAvailableSerials = async () => {
    setLoading(true);
    setError(null);
    try {
      const serials = await stcTransService.getSerialNumbersAtBin(itemNo, warehouseId, binCode);
      setAvailableSerials(serials);
      setFilteredSerials(serials);
      if (serials.length === 0) {
        setError(t('warehouse.modules.inventoryCorrection.serialBatch.noSerialsAvailableAtLocation', {}, 'Ingen serienumre tilgjengelig på denne lokasjonen'));
      }
    } catch (err: any) {
      console.error('Error loading serial numbers:', err);
      setError(err.message || t('warehouse.modules.inventoryCorrection.serialBatch.couldNotFetchSerials', {}, 'Kunne ikke hente serienumre'));
    } finally {
      setLoading(false);
    }
  };

  // --- Remove-modus logikk ---
  const toggleSerial = (serialNo: string) => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (next.has(serialNo)) {
        next.delete(serialNo);
      } else {
        next.add(serialNo);
      }
      return next;
    });
  };

  const handleRemoveScanInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const exactMatch = availableSerials.find(
        s => s.serialNo.toLowerCase() === searchTerm.trim().toLowerCase()
      );
      if (exactMatch) {
        toggleSerial(exactMatch.serialNo);
        setSearchTerm('');
      } else {
        setError(t('warehouse.modules.inventoryCorrection.serialBatch.serialNotAtLocation', {}, 'Serienummeret finnes ikke på denne lokasjonen'));
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // --- Add-modus logikk ---
  const addSerialNumber = useCallback(async () => {
    const trimmed = currentInput.trim();
    if (!trimmed) return;
    setError(null);

    // Lokal duplikatsjekk
    if (enteredSerials.includes(trimmed)) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.serialAlreadyInList', { '0': trimmed }, `Serienummer "${trimmed}" er allerede lagt til i listen`));
      return;
    }

    // Backend duplikatsjekk
    setChecking(true);
    try {
      const exists = await stcTransService.checkSerialNumberExists(itemNo, trimmed);
      if (exists) {
        setError(t('warehouse.modules.inventoryCorrection.serialBatch.serialAlreadyRegistered', { '0': trimmed, '1': itemNo }, `Serienummer "${trimmed}" er allerede registrert for vare ${itemNo}`));
        setChecking(false);
        return;
      }
    } catch (err) {
      console.error('Feil ved serienummersjekk:', err);
      // Tillat offline-registrering
    }
    setChecking(false);

    setEnteredSerials(prev => [...prev, trimmed]);
    setCurrentInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [currentInput, enteredSerials, itemNo, t]);

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addSerialNumber();
    }
  };

  const removeEnteredSerial = (index: number) => {
    setEnteredSerials(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // --- Bekreft ---
  const handleConfirm = () => {
    if (mode === 'remove') {
      if (selectedSerials.size === 0) return;
      onComplete(Array.from(selectedSerials));
    } else {
      if (enteredSerials.length === 0) return;
      onComplete(enteredSerials);
    }
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const isRemove = mode === 'remove';
  const serialCount = isRemove ? selectedSerials.size : enteredSerials.length;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isRemove ? 'bg-red-50' : 'bg-purple-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isRemove ? 'bg-red-100' : 'bg-purple-100'}`}>
              <Hash className={isRemove ? 'text-red-600' : 'text-purple-600'} size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isRemove ? t('warehouse.modules.inventoryCorrection.serialBatch.selectSerialsForRemoval', {}, 'Velg serienumre for uttak') : t('warehouse.modules.inventoryCorrection.serialBatch.registerSerials', {}, 'Registrer serienumre')}
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item/Location info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{t('warehouse.general.location')}: <span className="font-medium text-gray-900">{binCode}</span></span>
            {isRemove && availableSerials.length > 0 && (
              <span>{t('warehouse.general.available')}: <span className="font-medium text-gray-900">{availableSerials.length}</span></span>
            )}
          </div>
        </div>

        {/* Search/input */}
        <div className="px-4 pb-3">
          {isRemove ? (
            /* Remove-modus: Søkefelt */
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setError(null); }}
                onKeyDown={handleRemoveScanInput}
                placeholder={t('warehouse.modules.inventoryCorrection.serialBatch.scanOrSearchSerial', {}, 'Skann eller søk serienummer...')}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
            </div>
          ) : (
            /* Add-modus: Input for nye serienumre */
            <div className="flex gap-2">
              <div className="relative flex-1">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  ref={inputRef}
                  type="text"
                  value={currentInput}
                  onChange={(e) => { setCurrentInput(e.target.value); setError(null); }}
                  onKeyDown={handleAddKeyDown}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder={t('warehouse.modules.inventoryCorrection.serialBatch.scanOrTypeSerial', {}, 'Skann eller skriv serienummer...')}
                  disabled={checking}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                  autoComplete="off"
                />
              </div>
              <button
                onClick={addSerialNumber}
                disabled={!currentInput.trim() || checking}
                className="px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {checking ? <Loader2 className="animate-spin" size={16} /> : t('common.buttons.add')}
              </button>
            </div>
          )}

          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {isRemove ? (
            /* Remove-modus: Lista med checkboxes */
            loading ? (
              <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
                <Loader2 className="animate-spin" size={20} />
                {t('warehouse.modules.inventoryCorrection.serialBatch.fetchingSerials', {}, 'Henter serienumre...')}
              </div>
            ) : filteredSerials.length === 0 && !error ? (
              <div className="text-center text-gray-500 py-8">
                {searchTerm ? t('warehouse.modules.inventoryCorrection.serialBatch.noSearchMatch', {}, 'Ingen treff på søket') : t('warehouse.modules.inventoryCorrection.serialBatch.noSerialsAvailable', {}, 'Ingen serienumre tilgjengelig')}
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredSerials.map((serial) => {
                  const isSelected = selectedSerials.has(serial.serialNo);
                  return (
                    <button
                      key={serial.serialNo}
                      onClick={() => toggleSerial(serial.serialNo)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="text-white" size={14} />}
                        </div>
                        <span className="font-mono font-medium text-gray-900">{serial.serialNo}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            /* Add-modus: Registrerte serienumre */
            enteredSerials.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ScanLine size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('warehouse.modules.inventoryCorrection.serialBatch.noSerialsRegisteredYet', {}, 'Ingen serienumre registrert ennå')}</p>
                <p className="text-xs mt-1">{t('warehouse.modules.inventoryCorrection.serialBatch.scanSerialsAbove', {}, 'Skann eller skriv inn serienumre ovenfor')}</p>
              </div>
            ) : (
              <div className="space-y-1">
                {enteredSerials.map((sn, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-400 w-6 text-right">{index + 1}.</span>
                      <span className="font-mono text-sm text-gray-900">{sn}</span>
                    </div>
                    <button
                      onClick={() => removeEnteredSerial(index)}
                      className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      title={t('warehouse.modules.inventoryCorrection.serialBatch.removeSerial', {}, 'Fjern serienummer')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={serialCount === 0 || confirming}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              serialCount > 0
                ? isRemove
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {confirming ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
            {isRemove
              ? t('warehouse.modules.inventoryCorrection.serialBatch.confirmRemoval', { '0': serialCount }, `Bekreft uttak (${serialCount})`)
              : t('warehouse.modules.inventoryCorrection.serialBatch.confirmRegistration', { '0': serialCount }, `Bekreft registrering (${serialCount})`)
            }
          </button>
        </div>
      </div>
    </div>
  );
};
